"use strict";
/**
 * Free throws pending, derived from NBA taps (ai/nba/N8-taps/PLAN.md §10.1). One transition
 * implementation for the pad's display (its own session, in tap order) and the server's received
 * state (every available tap, in processing order: the candidate production field). The two
 * states can still differ: their inputs and arrival times differ.
 *
 * `unknown` is withheld, never zero. Ambiguity always lands there; only a new trip start, a
 * clearing event, `period_next` or a valid UNDO leaves it.
 *
 * Technical free throws (§12.6, A198) are held beside the trip: `ft_tech` pends one awarded
 * attempt, and only an explicit `ft_tech_made` / `ft_tech_missed` resolves it. Nothing is inferred
 * from the order of shots; any other sequence is unknown. A step is `{trip, tech, reason}`: the old
 * `state` is renamed so a reader unaware of technicals fails instead of reading `none`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.nbaUndoReachable = exports.FT_PASS_KINDS = exports.FT_CLEARING_KINDS = exports.FT_TRIP_ATTEMPTS = exports.ftComplete = exports.ftNothingPending = exports.FT_VERSION = void 0;
exports.ftTransition = ftTransition;
exports.ftValidUndos = ftValidUndos;
exports.deriveFtPending = deriveFtPending;
const tap_vocabulary_1 = require("./tap-vocabulary");
/**
 * The fold rule's version, carried by the endpoint and the export: 3 adds pad version 2's kinds
 * (N8 §19.4). The step shape `{trip, tech, reason}` has not changed since 2.
 */
exports.FT_VERSION = 3;
/** No free throws pending at all: never while a technical attempt is pending. */
const ftNothingPending = (s) => s.trip.kind === "none" && s.tech === null;
exports.ftNothingPending = ftNothingPending;
/** Complete enough for production: the trip is known. A known technical over `unknown` is not. */
const ftComplete = (s) => s.trip.kind !== "unknown";
exports.ftComplete = ftComplete;
exports.FT_TRIP_ATTEMPTS = { ft_trip_1: 1, ft_trip_2: 2, ft_trip_3: 3 };
/** Live-ball events that cannot happen with free throws of the same trip still to come. */
exports.FT_CLEARING_KINDS = new Set([
    "made_2", "made_3", "turnover", "def_rebound", "shot_2", "shot_3", "missed", "ball", "off_rebound",
]);
/** Kinds that never touch the trip, from any session. */
exports.FT_PASS_KINDS = new Set(["pull", "resume", "tip_confirm", ...tap_vocabulary_1.NBA_TRACK_KINDS, ...tap_vocabulary_1.NBA_CLOCK_KINDS]);
const ATTEMPTS = new Set(["ft_made", "ft_missed"]);
const TECH_ATTEMPTS = new Set(["ft_tech_made", "ft_tech_missed"]);
const NEVER_UNDONE = new Set(["undo", "session_start", "resume", "tip_confirm", ...tap_vocabulary_1.NBA_TRACK_KINDS, ...tap_vocabulary_1.NBA_CLOCK_KINDS]);
/**
 * Whether an UNDO may reach this tap, by the target's own pad version (N8 §19.4): PULL is
 * reachable only in version 1. Shared by ft-pending and the game-state fold.
 */
const nbaUndoReachable = (target) => !NEVER_UNDONE.has(target.kind) && (target.kind !== "pull" || (0, tap_vocabulary_1.nbaPadVersion)(target.padVersion) === 1);
exports.nbaUndoReachable = nbaUndoReachable;
const UNKNOWN = { kind: "unknown" };
const NONE = { kind: "none" };
/**
 * One tap of the current session through the transition table. Pure; the caller owns the session
 * rule and skips FT_PASS_KINDS before judging the session.
 */
function ftTransition(prev, t) {
    const s = prev.trip;
    const pending = prev.tech;
    /** Unknown; the pending technical is cleared unless the rule keeps it (§12.2). */
    const unknown = (why, keepTech = false) => ({ trip: UNKNOWN, tech: keepTech ? pending : null, reason: why });
    const known = (trip, tech = pending) => ({ trip, tech, reason: null });
    const trip = exports.FT_TRIP_ATTEMPTS[t.kind];
    if (exports.FT_PASS_KINDS.has(t.kind))
        return prev;
    if (t.kind === "undo")
        return unknown("invalid-undo"); // only invalid UNDOs reach the fold
    if (t.kind === "ft_unsure")
        return unknown("unsure");
    if (t.kind === "period_next")
        return known(NONE, null);
    if (t.kind === "ft_tech") {
        if (!t.side)
            return unknown("unrecognized");
        if (pending === null)
            return { ...prev, tech: { side: t.side, shots: 1 } };
        if (pending.side === t.side)
            return { ...prev, tech: { side: t.side, shots: pending.shots + 1 } };
        return unknown("tech-wrong-side");
    }
    if (TECH_ATTEMPTS.has(t.kind)) {
        if (pending !== null && t.side === pending.side)
            return { ...prev, tech: pending.shots > 1 ? { ...pending, shots: pending.shots - 1 } : null };
        return unknown("tech-attempt-without-tech");
    }
    if (pending !== null && ATTEMPTS.has(t.kind))
        return unknown("attempt-before-tech");
    if (pending !== null && exports.FT_CLEARING_KINDS.has(t.kind))
        return unknown("event-in-tech");
    if (trip !== undefined) {
        if (s.kind === "open")
            return unknown("overlapping-start", true);
        if (t.side)
            return known({ kind: "open", side: t.side, attempt: 1, of: trip });
        return unknown("unrecognized");
    }
    if (ATTEMPTS.has(t.kind)) {
        if (s.kind === "open") {
            if (t.side !== s.side)
                return unknown("wrong-side");
            return known(s.attempt >= s.of ? NONE : { ...s, attempt: s.attempt + 1 });
        }
        return s.kind === "none" ? unknown("attempt-without-trip") : prev;
    }
    if (exports.FT_CLEARING_KINDS.has(t.kind))
        return s.kind === "open" ? unknown("event-in-trip") : known(NONE);
    return unknown("unrecognized");
}
/** The sessions after a session_start, and whether it was a reset, unsynchronized or stale. */
function sessionStart(t, s) {
    if (t.clientOffsetMs === null || t.clientOffsetMs === undefined) {
        return { next: { ...s, current: null }, reason: "unsynchronized-start" };
    }
    const start = Date.parse(t.clientTs) + t.clientOffsetMs;
    if (!t.sessionId || !Number.isFinite(start) || (s.latestStart !== null && start <= s.latestStart))
        return { next: s, reason: "stale" };
    return { next: { current: t.sessionId, latestStart: start }, reason: "reset" };
}
const NO_SESSIONS = { current: null, latestStart: null };
function fold(seq) {
    let st = { trip: UNKNOWN, tech: null, reason: null };
    let sessions = NO_SESSIONS;
    for (const t of seq) {
        if (exports.FT_PASS_KINDS.has(t.kind))
            continue;
        if (t.kind === "session_start") {
            const r = sessionStart(t, sessions);
            sessions = r.next;
            st = { trip: UNKNOWN, tech: null, reason: r.reason };
        }
        else if (sessions.current === null || t.sessionId !== sessions.current)
            st = { trip: UNKNOWN, tech: null, reason: "stale" };
        else
            st = ftTransition(st, t);
    }
    return st;
}
/**
 * The valid UNDOs, each to the tap it undoes, judged in the order given (§10.1). The matcher's
 * correction audit is held to the same answers (model/tests/nba/fixtures/undo-validity.json).
 */
function ftValidUndos(taps) {
    const byId = new Map();
    const valid = new Map();
    const undone = new Set();
    let sessions = NO_SESSIONS;
    for (const t of taps) {
        if (byId.has(t.tapId))
            continue;
        byId.set(t.tapId, t);
        if (t.kind === "session_start")
            sessions = sessionStart(t, sessions).next;
        if (t.kind === "undo" && t.undoesTapId !== null) {
            const target = byId.get(t.undoesTapId);
            if (target !== undefined && target !== t && sessions.current !== null && t.sessionId === sessions.current
                && target.sessionId === t.sessionId && (0, exports.nbaUndoReachable)(target) && !undone.has(target.tapId)) {
                undone.add(target.tapId);
                valid.set(t.tapId, target.tapId);
            }
        }
    }
    return valid;
}
/**
 * The state after each received tap, in the order given. A repeated tap id is a retry and is
 * skipped. A valid UNDO re-derives from its own receipt onward, dropping every tap any valid
 * UNDO has undone, so earlier steps (what was shown then) never change.
 */
function deriveFtPending(taps) {
    const validUndos = ftValidUndos(taps); // each judged on the taps before it, so safe to compute first
    const seen = new Set();
    const undone = new Set();
    const received = [];
    const steps = [];
    for (const t of taps) {
        if (seen.has(t.tapId))
            continue;
        seen.add(t.tapId);
        const target = validUndos.get(t.tapId);
        if (target !== undefined)
            undone.add(target);
        received.push(t);
        const seq = received.filter((x) => !undone.has(x.tapId) && !validUndos.has(x.tapId));
        steps.push({ tapId: t.tapId, ...fold(seq) });
    }
    return steps;
}
